import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    TextField,
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';

export default function SearchBar({
    center,
    onSearchSelection,
    onGeolocation = false,
    iconSpinning = false,
    textInside = '',
    disabled = false,
}) {
    const [inputValue, setInputValue] = useState(textInside);
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);

    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const detailsCache = useRef(new Map());

    useEffect(() => {
        let isMounted = true;
        let retryTimer = null;

        const initializeGooglePlaces = () => {
            const googleMaps = window.google?.maps;
            const googlePlaces = googleMaps?.places;
            if (!googlePlaces?.AutocompleteService || !googlePlaces?.PlacesService) {
                if (isMounted) {
                    retryTimer = window.setTimeout(initializeGooglePlaces, 250);
                }
                return;
            }

            autocompleteService.current = new googlePlaces.AutocompleteService();
            placesService.current = new googlePlaces.PlacesService(document.createElement('div'));
        };

        initializeGooglePlaces();

        return () => {
            isMounted = false;
            if (retryTimer) window.clearTimeout(retryTimer);
        };
    }, []);

    useEffect(() => {
        setInputValue(textInside || '');
        setSelectedOption(null);
    }, [textInside]);

    const fetchDetails = (prediction) => {
        const placesStatus = window.google?.maps?.places?.PlacesServiceStatus;
        if (!placesService.current || !placesStatus) {
            return Promise.resolve(null);
        }

        if (detailsCache.current.has(prediction.place_id)) {
            return Promise.resolve(detailsCache.current.get(prediction.place_id));
        }

        return new Promise((resolve) => {
            placesService.current.getDetails(
                {
                    placeId: prediction.place_id,
                    fields: [
                        'place_id',
                        'name',
                        'formatted_address',
                        'photos',
                        'opening_hours',
                        'types',
                        'geometry',
                    ],
                },
                (details, status) => {
                    if (
                        status !== placesStatus.OK ||
                        !details
                    ) {
                        resolve(null);
                        return;
                    }

                    const formatted = {
                        label: details.name,
                        placeId: details.place_id,
                        name: details.name,
                        address: details.formatted_address,
                        openingHours:
                            details.opening_hours?.weekday_text || null,
                        photo: details.photos
                            ? details.photos[0].getUrl({
                                maxWidth: 200,
                                maxHeight: 200,
                            })
                            : null,
                        location: details.geometry?.location?.toJSON(),
                        types: details.types,
                    };

                    detailsCache.current.set(
                        prediction.place_id,
                        formatted
                    );
                    resolve(formatted);
                }
            );
        });
    };

    const fetchPredictions = (value) => {
        const googleMaps = window.google?.maps;
        const placesStatus = googleMaps?.places?.PlacesServiceStatus;
        if (!autocompleteService.current || !placesService.current || !googleMaps?.LatLng || !placesStatus) {
            return Promise.resolve([]);
        }

        return new Promise((resolve) => {
            autocompleteService.current.getPlacePredictions(
                {
                    input: value,
                    types: ['establishment', 'geocode'],
                    ...(center?.lat && center?.lng
                        ? {
                            location: new googleMaps.LatLng(center.lat, center.lng),
                            radius: 25000,
                        }
                        : {}),
                },
                async (predictions, status) => {
                    if (
                        status !== placesStatus.OK ||
                        !predictions
                    ) {
                        resolve([]);
                        return;
                    }

                    const enriched = await Promise.all(
                        predictions.slice(0, 6).map(fetchDetails)
                    );

                    resolve(enriched.filter(Boolean));
                }
            );
        });
    };

    const handleInputChange = async (value) => {
        setInputValue(value);
        setSelectedOption(null);

        if (!value || value.trim().length < 2) {
            setOptions([]);
            return;
        }

        const enriched = await fetchPredictions(value);
        setOptions(enriched);
    };

    const handleSelect = (option) => {
        if (!option || typeof option === 'string') return;
        setSelectedOption(option);
        setInputValue(option.name || option.label || '');
        onSearchSelection(option);
    };

    const handleGo = async () => {
        if (selectedOption) {
            onSearchSelection(selectedOption);
            return;
        }

        if (options[0]) {
            handleSelect(options[0]);
            return;
        }

        if (!inputValue || inputValue.trim().length < 2) return;

        const enriched = await fetchPredictions(inputValue);
        setOptions(enriched);
        if (enriched[0]) {
            handleSelect(enriched[0]);
        }
    };

    const getTodayOpeningHours = (openingHours) => {
        if (!openingHours) return null;

        const today = new Date().getDay();
        const days = [
            'Domenica',
            'Lunedì',
            'Martedì',
            'Mercoledì',
            'Giovedì',
            'Venerdì',
            'Sabato',
        ];

        const todayText =
            openingHours[today === 0 ? 6 : today - 1];

        if (!todayText) return null;

        return `${days[today]}: ${todayText.split(':').slice(1).join(':').trim()}`;
    };

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, p: 0.75,
            bgcolor: 'background.paper', borderRadius: '14px',
            boxShadow: '0 4px 20px rgba(29, 29, 27, 0.06)',
            '&:focus-within': { boxShadow: '0 0 0 2px rgba(138, 0, 48, 0.22)' },
        }}>
            <IconButton
                aria-label="Cerca"
                onClick={handleGo}
                disabled={disabled || inputValue.trim().length < 2}
                sx={{ width: 44, height: 44, color: 'primary.main', '&.Mui-disabled': { color: 'text.secondary' } }}
            >
                <SearchIcon fontSize="small" />
            </IconButton>
            <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
                <Autocomplete
                    disabled={disabled}
                    freeSolo
                    options={options}
                    inputValue={inputValue}
                    value={null}
                    onInputChange={(e, newValue) =>
                        handleInputChange(newValue)
                    }
                    onChange={(e, newValue) => handleSelect(newValue)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            handleGo();
                        }
                    }}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.label || option.name || ''}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Cerca una zona o un locale"
                            inputProps={{ ...params.inputProps, 'aria-label': 'Cerca una zona o un locale' }}
                            fullWidth
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'transparent',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    minHeight: 44,
                                    '&.MuiAutocomplete-inputRoot': { p: '0 !important' },
                                    '& fieldset': { border: 'none' },
                                    '& input::placeholder': { color: 'text.secondary', opacity: 1 },
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <Box
                            {...props}
                            sx={{
                                display: 'flex',
                                gap: 1,
                                py: 1,
                                alignItems: 'center',
                            }}
                        >
                            <Box sx={{ width: 90 }}>
                                {option.photo && (
                                    <img
                                        src={option.photo}
                                        alt={option.name}
                                        style={{
                                            width: '100%',
                                            height: 70,
                                            objectFit: 'cover',
                                            borderRadius: 8,
                                        }}
                                    />
                                )}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {option.name}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {option.address}
                                </Typography>

                                {option.openingHours && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {getTodayOpeningHours(
                                            option.openingHours
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                />
            </Box>

            {onGeolocation && (
                <IconButton
                    aria-label="Usa la mia posizione"
                    disabled={disabled}
                    sx={{
                        width: 44,
                        height: 44,
                        alignSelf: 'center',
                        color: 'primary.main',
                        animation: iconSpinning
                            ? 'spin 2s linear infinite'
                            : 'none',
                        '@keyframes spin': {
                            '0%': {
                                transform:
                                    'rotate(0deg)',
                            },
                            '100%': {
                                transform:
                                    'rotate(360deg)',
                            },
                        },
                    }}
                    onClick={() => {
                        setInputValue('');
                        setOptions([]);
                        setSelectedOption(null);
                        onGeolocation();
                    }}
                >
                    <MyLocationIcon />
                </IconButton>
            )}
        </Box>
    );
}
